import { isPlatformBrowser } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, Signal, computed, inject } from '@angular/core';
import { type IToggle, UnleashClient } from 'unleash-proxy-client';
import { type FeatureFlags } from '../models/feature-types';
import { FeatureVariant } from '../models/feature-variant.model';
import { EnvironmentService } from './environment.service';

const UNLEASH_REFRESH_INTERVAL_IN_SECONDS = 60;
const UNLEASH_METRICS_INTERVAL_IN_SECONDS = 300;

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagsService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly environment = inject(EnvironmentService);
    private readonly ngZone = inject(NgZone);

    private customFeatureFlagConfig: IToggle[] = [];
    private unleash?: UnleashClient;

    public async initUnleash(appName: string): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        this.customFeatureFlagConfig = JSON.parse(localStorage.getItem('custom-feature-flags') ?? '[]') as IToggle[];

        if (!this.environment.unleashClientKey) {
            return;
        }

        // We need to run unleash.start() outside of ngZone during app init
        // Otherwise the app hydration will not become stable due to an internal unleash setInterval call
        return this.ngZone.runOutsideAngular(async () => {
            this.unleash = new UnleashClient({
                url: `${this.environment.baseHref}/api/feature-flags`,
                clientKey: this.environment.unleashClientKey,
                appName,
                refreshInterval: UNLEASH_REFRESH_INTERVAL_IN_SECONDS,
                metricsInterval: UNLEASH_METRICS_INTERVAL_IN_SECONDS,
            });

            // Start the background polling
            return this.unleash.start();
        });
    }

    public isFeatureEnabled(flag: FeatureFlags): Signal<boolean> {
        return computed(() => {
            const unleashFlag = this.unleash?.isReady() ? this.unleash.isEnabled(flag) : false;

            if (this.customFeatureFlagConfig.length > 0) {
                const customFlag = this.customFeatureFlagConfig.find(f => f.name === flag);

                return customFlag ? customFlag.enabled : unleashFlag;
            }

            return unleashFlag;
        });
    }

    public getVariant(flag: FeatureFlags): FeatureVariant | undefined {
        if (!this.unleash?.isReady()) {
            return undefined;
        }

        const variant = this.unleash.getVariant(flag);

        return variant.enabled ? { name: variant.name, payload: variant.payload } : undefined;
    }
}
